<script lang="ts">
  import '$features/workspace/styles/auth-guest.css';
  import {
    createSpeechToTextController,
    isSpeechToTextSupported,
    type SpeechToTextStatus,
  } from '$features/campaigns/speech/speech-to-text';
  import { IconButton } from '$features/workspace/ui';
  import { CircleAlert, Mic, Square, Wand2 } from 'lucide-svelte';
  import { onDestroy } from 'svelte';

  interface Props {
    id?: string;
    value?: string;
    rows?: number;
    placeholder?: string;
    showHint?: boolean;
    showSpeech?: boolean;
    showEnhance?: boolean;
    canEnhance?: boolean;
    enhancing?: boolean;
    dictateAriaLabel?: string;
    stopAriaLabel?: string;
    enhanceAriaLabel?: string;
    enhanceError?: string;
    /** Iris marks CLARA only, so the dictate button is neutral unless this is a CLARA reflection. */
    tone?: 'neutral' | 'clara';
    invalid?: boolean;
    describedBy?: string;
    onValueChange?: (value: string) => void;
    onEnhance?: () => void;
  }

  let {
    id,
    value = $bindable(''),
    rows = 4,
    placeholder = 'Type your response',
    showHint = true,
    showSpeech = true,
    showEnhance = false,
    canEnhance = false,
    enhancing = false,
    dictateAriaLabel = 'Dictate answer',
    stopAriaLabel = 'Stop dictation',
    enhanceAriaLabel = 'Enhance with AI',
    enhanceError = '',
    tone = 'neutral',
    invalid = false,
    describedBy,
    onValueChange,
    onEnhance,
  }: Props = $props();

  function handleTextChange(nextText: string) {
    value = nextText;
    onValueChange?.(nextText);
  }

  const speechSupported = isSpeechToTextSupported();
  const speechEnabled = $derived(showSpeech && speechSupported);
  const enhanceEnabled = $derived(showEnhance && !!onEnhance);
  const showEnhanceButton = $derived(enhanceEnabled);
  const showMicButton = $derived(speechEnabled);
  const showActionButtons = $derived(showEnhanceButton || showMicButton);

  const speech = createSpeechToTextController();

  let isListening = $state(false);
  let speechError = $state('');

  function handleStatusChange(status: SpeechToTextStatus, errorMessage: string | null) {
    isListening = status === 'listening';
    speechError = errorMessage ?? '';
  }

  function toggleSpeech() {
    if (isListening) {
      speech.stop();
      return;
    }

    speech.start(value, handleTextChange, handleStatusChange);
  }

  onDestroy(() => {
    speech.dispose();
  });
</script>

<div class="ag-answer">
  <div class="stat-field__control">
    <textarea
      {id}
      class="stat-textarea"
      {rows}
      {placeholder}
      {value}
      aria-invalid={invalid ? 'true' : undefined}
      aria-describedby={describedBy || undefined}
      oninput={(e) => handleTextChange((e.currentTarget as HTMLTextAreaElement).value)}
    ></textarea>

    {#if showActionButtons}
      <span class="stat-dictate">
        {#if showMicButton}
          <IconButton
            label={isListening ? stopAriaLabel : dictateAriaLabel}
            aria-pressed={isListening}
            class={[
              tone === 'clara' ? 'stat-dictate__button' : '',
              isListening ? (tone === 'clara' ? 'stat-dictate__button--live' : 'ag-answer__mic--live') : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onclick={toggleSpeech}
          >
            {#if isListening}
              <Square size={16} fill="currentColor" />
            {:else}
              <Mic size={20} />
            {/if}
          </IconButton>
        {/if}

        {#if showEnhanceButton}
          <IconButton label={enhanceAriaLabel} disabled={!canEnhance || enhancing} onclick={() => onEnhance?.()}>
            {#if enhancing}
              <span class="stat-spinner stat-spinner--small" aria-hidden="true"></span>
            {:else}
              <Wand2 size={20} />
            {/if}
          </IconButton>
        {/if}
      </span>
    {/if}
  </div>

  {#if speechEnabled && showHint}
    <p class="stat-meta" aria-live="polite">
      {#if isListening}
        Listening. Select stop when you’re finished. New words are added to the end.
      {:else}
        Select the microphone to dictate. New words are added after your text.
      {/if}
    </p>
  {/if}

  {#if speechError}
    <p class="stat-field__error" role="alert">
      <CircleAlert size={16} aria-hidden="true" />
      <span>{speechError}</span>
    </p>
  {/if}

  {#if enhanceError}
    <p class="stat-field__error">
      <CircleAlert size={16} aria-hidden="true" />
      <span>{enhanceError}</span>
    </p>
  {/if}
</div>
